/* eslint-disable */
import {
  EnhancedHeader,
  EnhancedHomePage,
  EnhancedFooter
} from './enhanced-landing-pages.js';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import { ProposalModal } from './proposal-modal';
import { HireModal } from './hire-modal';
import { MessageModal } from './message-modal';
import { mockFreelancers } from './sample-data';
import { mockJobs as sampleJobs } from './sample-jobs';
import { TrustBadgeGroup, TrustScore } from './components/trust/TrustBadge';

// Mock jobs kept minimal for demo
const mockJobs = sampleJobs;

// Enhanced Header Component (Exported from enhanced-landing-pages.js)
export const Header = EnhancedHeader;
export const HomePage = EnhancedHomePage;

// JobsPage (unchanged apart from imports)
export const JobsPage = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showProposal, setShowProposal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);

  const categories = ['all','Web Development','Mobile Development','Design','Writing','Marketing','Data Science'];

  const filteredJobs = mockJobs.filter(job => {
    const matchesSearch = job.title.toLowerCase().includes(searchTerm.toLowerCase()) || job.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || job.skills.some(skill => skill.toLowerCase().includes(selectedCategory.toLowerCase()));
    return matchesSearch && matchesCategory;
  });

  const openProposal = (job) => { setSelectedJob(job); setShowProposal(true); };
  const onProposalSubmitted = () => { setShowProposal(false); if (selectedJob?.id) navigate(`/jobs/${selectedJob.id}`); };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Find Work</h1>
          <div className="flex gap-4 mb-6">
            <div className="flex-1">
              <input type="text" placeholder="Search for jobs..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent" />
            </div>
            <button className="bg-black text-white px-8 py-3 rounded-lg hover:bg-gray-800 font-medium">Search</button>
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map(category => (
              <button key={category} onClick={() => setSelectedCategory(category)} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${selectedCategory === category ? 'bg-black text-white' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'}`}>{category === 'all' ? 'All Categories' : category}</button>
            ))}
          </div>
        </div>

        <div className="space-y-6">
          {filteredJobs.map(job => (
            <div key={job.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 hover:text-black cursor-pointer">{job.title}</h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">{job.description}</p>
                  <div className="flex flex-wrap gap-2 mb-4">{job.skills.map(skill => (<span key={skill} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">{skill}</span>))}</div>
                  <div className="flex items-center gap-6 text-sm text-gray-600"><span className="font-medium text-black">{job.budget}</span><span>{job.duration}</span><span>{job.postedTime}</span></div>
                </div>
                <button onClick={() => openProposal(job)} className="bg-black text-white px-6 py-2 rounded-lg hover:bg-gray-800 font-medium ml-6">Apply Now</button>
              </div>
              <div className="flex items-center pt-4 border-t border-gray-100">
                <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center mr-3"><span className="text-gray-600 font-medium">{job.client.name[0]}</span></div>
                <div>
                  <p className="font-medium text-gray-900">{job.client.name}</p>
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <span className="flex items-center"><svg className="w-4 h-4 text-yellow-400 mr-1" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>{job.client.rating}</span>
                    <span>{job.client.spent}</span><span>{job.client.location}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {showProposal && selectedJob && (<ProposalModal job={{ id: selectedJob.id, title: selectedJob.title, job_type: selectedJob.job_type || 'fixed_price' }} onClose={() => setShowProposal(false)} onSubmitted={onProposalSubmitted} />)}
    </div>
  );
};

// Enhanced Talent Page (sorting + pagination + hire/message modals)
export const EnhancedTalentPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // initialize from URL
  const initialQ = searchParams.get('q') || '';
  const initialSkill = searchParams.get('skill') || 'all';
  const initialSort = searchParams.get('sort') || 'relevance';
  const initialPage = parseInt(searchParams.get('page') || '1', 10);

  const [searchTerm, setSearchTerm] = useState(initialQ);
  const [selectedSkill, setSelectedSkill] = useState(initialSkill);
  const [sortBy, setSortBy] = useState(initialSort);
  const [page, setPage] = useState(initialPage > 0 ? initialPage : 1);
  const [hireTarget, setHireTarget] = useState(null);
  const [msgTarget, setMsgTarget] = useState(null);
  const urlJobId = searchParams.get('jobId') || '';

  const pageSize = 9;
  const allSkills = Array.from(new Set(mockFreelancers.flatMap(f => f.skills || [])));
  const skills = ['all', ...allSkills.slice(0, 12)];

  const filtered = mockFreelancers.filter(f => {
    const q = searchTerm.toLowerCase();
    const byText = f.name.toLowerCase().includes(q) || f.title.toLowerCase().includes(q);
    const bySkill = selectedSkill === 'all' || (f.skills || []).some(s => s.toLowerCase().includes(selectedSkill.toLowerCase()));
    return byText && bySkill;
  });

  const sorted = [...filtered].sort((a,b) => {
    const ar = a.rate || parseFloat(String(a.hourlyRate||'').replace(/[^0-9.]/g,'')) || 0;
    const br = b.rate || parseFloat(String(b.hourlyRate||'').replace(/[^0-9.]/g,'')) || 0;
    if (sortBy === 'rate_desc') return br - ar;
    if (sortBy === 'rate_asc') return ar - br;
    if (sortBy === 'rating_desc') return (b.rating||0) - (a.rating||0);
    if (sortBy === 'jobs_desc') return (b.completedJobs||0) - (a.completedJobs||0);
    return 0; // relevance
  });

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const current = sorted.slice((page-1)*pageSize, page*pageSize);

  useEffect(()=>{ setPage(1); }, [searchTerm, selectedSkill, sortBy]);

  // persist to URL whenever these change
  useEffect(()=>{
    const params = new URLSearchParams();
    if (searchTerm) params.set('q', searchTerm);
    if (selectedSkill && selectedSkill!=='all') params.set('skill', selectedSkill);
    if (sortBy && sortBy!=='relevance') params.set('sort', sortBy);
    params.set('page', String(page));
    // Preserve jobId (and optional ctype/amt) so Invite-to-Job flows remain shareable
    const current = new URLSearchParams(window.location.search);
    const jid = current.get('jobId');
    const ctype = current.get('ctype');
    const amt = current.get('amt');
    if (jid) params.set('jobId', jid);
    if (ctype) params.set('ctype', ctype);
    if (amt) params.set('amt', amt);
    setSearchParams(params, { replace: true });
  }, [searchTerm, selectedSkill, sortBy, page, setSearchParams]);

  const jobId = (new URLSearchParams(window.location.search)).get('jobId');

  const openMessage = (freelancer) => {
    setMsgTarget(freelancer);
  };

  const handleMessageSent = ({ freelancerId, message }) => {
    const items = JSON.parse(localStorage.getItem('mock_messages') || '[]');
    const f = mockFreelancers.find(x => String(x.id) === String(freelancerId));
    items.push({ freelancerId, freelancerName: f?.name || 'Candidate', message, at: Date.now() });
    localStorage.setItem('mock_messages', JSON.stringify(items));
    setMsgTarget(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div className="flex-1">
            <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3">Find Talent</h1>
            <div className="flex gap-4 mb-4">
              <input type="text" placeholder="Search for freelancers..." value={searchTerm} onChange={(e)=>setSearchTerm(e.target.value)} className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent" />
              <select value={sortBy} onChange={(e)=>setSortBy(e.target.value)} className="px-4 py-3 border border-gray-300 rounded-lg">
                <option value="relevance">Sort: Relevance</option>
                <option value="rate_desc">Rate: High → Low</option>
                <option value="rate_asc">Rate: Low → High</option>
                <option value="rating_desc">Rating: High → Low</option>
                <option value="jobs_desc">Jobs Completed: High → Low</option>
              </select>
            </div>
            <div className="text-sm text-gray-600">{filtered.length} freelancers • Page {page} of {totalPages}</div>
          </div>
          <div className="flex flex-wrap gap-2">
            {skills.map(skill => (
              <button key={skill} onClick={() => setSelectedSkill(skill)} className={`px-3 py-2 rounded-full text-sm font-medium ${selectedSkill===skill ? 'bg-black text-white' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'}`}>{skill==='all' ? 'All Skills' : skill}</button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {current.map(freelancer => (
            <div key={freelancer.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
              <div className="text-center mb-4">
                <img src={freelancer.avatar} alt={freelancer.name} className="w-20 h-20 rounded-full mx-auto mb-4 object-cover" />
                <div className="flex items-center justify-center gap-2 flex-wrap">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 font-heading">{freelancer.name}</h3>
                  <TrustScore user={freelancer} size="xs" showLabel={false} />
                </div>
                <div className="flex justify-center mt-2">
                  <TrustBadgeGroup user={freelancer} max={3} size="xs" short showLabel={false} />
                </div>
                <p className="text-gray-600 mb-1">{freelancer.title}</p>
                <p className="text-gray-900 font-semibold text-lg">${freelancer.rate || 0}/hr</p>
              </div>

              <div className="mb-4">
                <div className="flex items-center justify-center gap-4 text-sm text-gray-600 mb-3">
                  <span className="flex items-center"><svg className="w-4 h-4 text-yellow-400 mr-1" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>{freelancer.rating}</span>
                  <span>{freelancer.completedJobs} jobs</span>
                </div>
                <p className="text-gray-600 text-sm text-center mb-4">{freelancer.description}</p>
                <div className="flex flex-wrap gap-1 mb-4 justify-center">{(freelancer.skills||[]).slice(0,5).map(skill => (<span key={skill} className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">{skill}</span>))}</div>
                {freelancer.portfolio && (
                  <div className="bg-gray-50 rounded-lg p-3 text-left">
                    <div className="text-xs text-gray-500 mb-1">Portfolio</div>
                    {(freelancer.portfolio || []).slice(0,2).map((p, i) => (<div key={i} className="text-sm text-gray-800">• {p.title} <span className="text-gray-500">({p.tag})</span></div>))}
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <button onClick={() => navigate(`/freelancers/${freelancer.id}`)} className="flex-1 border border-black text-black py-2 rounded-lg hover:bg-gray-50 font-medium">View Profile</button>
                <button onClick={() => openMessage(freelancer)} className="flex-1 border border-gray-300 text-black py-2 rounded-lg hover:bg-gray-50 font-medium">Message</button>
                <button onClick={() => setHireTarget(freelancer)} className="flex-1 bg-black text-white py-2 rounded-lg hover:bg-gray-800 font-medium">{urlJobId ? 'Invite to Job' : 'Hire'}</button>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        <div className="mt-8 flex items-center justify-center gap-2">
          <button onClick={() => setPage(p => Math.max(1, p-1))} className="px-3 py-2 border border-gray-300 rounded hover:bg-gray-50">Prev</button>
          {Array.from({length: totalPages}).map((_, i) => (
            <button key={i} onClick={() => setPage(i+1)} className={`px-3 py-2 rounded border ${page===i+1 ? 'bg-black text-white border-black' : 'border-gray-300 hover:bg-gray-50'}`}>{i+1}</button>
          ))}
          <button onClick={() => setPage(p => Math.min(totalPages, p+1))} className="px-3 py-2 border border-gray-300 rounded hover:bg-gray-50">Next</button>
        </div>
      </div>

      {hireTarget && <HireModal freelancer={hireTarget} jobs={mockJobs} onClose={() => setHireTarget(null)} onSent={() => setHireTarget(null)} initial={{
        jobId: (new URLSearchParams(window.location.search)).get('jobId') || '',
        contractType: (new URLSearchParams(window.location.search)).get('ctype') || undefined,
        amount: (new URLSearchParams(window.location.search)).get('amt') || ''
      }} />}

      {msgTarget && <MessageModal freelancer={msgTarget} onClose={() => setMsgTarget(null)} onSent={handleMessageSent} />}
    </div>
  );
};